import React, { useState } from 'react';
import { Modal, Row, Col, Form } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faSatelliteDish } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { Button } from 'components/ui/button';
// Use vanilla axios because the backend expects form post data, not json
import axios from 'axios';
import qs from 'qs';
import Editor from 'helpers/editor';
import Select from 'react-select';
import { showSnackbar } from 'components/ui/snackbar/helper';

export default function SendRepEmailModal({
  afterSend,
  companyUsers,
  partnerNames,
  previouslySent,
  sendEmailSyndicationPath,
  show,
  onHide
}) {
  {/*
    TODO: change regular_flow -> diffs_enabled to match logic in syndication/mailer.rb
   */}

  const [manuallySentBefore, setManuallySentBefore] = useState(null);
  const [sending, setSending] = useState(false);
  const [replyToEmail, setReplyToEmail] = useState('');
  const [message, setMessage] = useState('');

  // NOTE: A reusable utility function `joinWithAnd` is available in helpers/formatters/string_formats
  // Consider refactoring this inline implementation to use that shared utility
  let partnerNamesDisplay;
  if (partnerNames.length === 1) {
    partnerNamesDisplay = partnerNames[0];
  } else if (partnerNames.length === 2) {
    partnerNamesDisplay = partnerNames.join(' and ');
  } else {
    partnerNamesDisplay = `${partnerNames.slice(0, -1).join(', ')}, and ${partnerNames.slice(-1)}`;
  }
  // `regularFlow`:
  // true: treat the email as new if we don't have a record of sending and as an update if we do
  // false: treat the email as an update even if we don't have a record of sending
  const regularFlow = (() => {
    if (previouslySent) {
      return true;
    } else {
      switch (manuallySentBefore) {
      case true:
        return false;
      case false:
        return true;
      default:
        return null;
      }
    }
  })();
  const sendingDisabled = regularFlow === null || sending;

  const postDontSend = () => {
    hideModal();

    axios.post(sendEmailSyndicationPath, qs.stringify({ send_email: false })).catch(() => {
      // don't interrupt the user, but trigger an error in the console
      throw new Error(`'postDontSend': Unexpected error posting to ${sendEmailSyndicationPath}`);
    });
  };

  const postSendUpdates = () => {
    if (regularFlow === null) { // should never happen
      activateSnackbar({ text: 'Please select an option.', variant: 'warning' });
      return;
    }
    setSending(true);

    axios.post(
      sendEmailSyndicationPath,
      qs.stringify({ send_email: true, regular_flow: regularFlow, email: replyToEmail, message: message })
    ).then(() => {
      hideModal();
      broadcastAfterSend();
      activateSnackbar({ text: 'Email will be sent shortly!', variant: 'success' });
    }).catch(() => {
      setSending(false);
      activateSnackbar({
        text: 'An error occurred while sending the email. Please try again later.',
        variant: 'warning'
      });
    });
  };

  const broadcastAfterSend = () => {
    afterSend();
    window.dispatchEvent(new CustomEvent('sendRepEmailModal:send'));
  };

  const hideModal = () => {
    setSending(false);
    setManuallySentBefore(null);
    onHide();
  };

  const textInputs = () => {
    const primaryBroker = companyUsers.filter(user => user.primaryBroker);

    return (
      <Row className="mx-0 px-0 mb-5 w-100">
        <Col className="px-0">
          <Form.Group>
            <Form.Label>
              Reply To Email Address
            </Form.Label>
            <Select
              className="mb-3"
              defaultValue={primaryBroker}
              getOptionValue={options => options.value}
              menuPlacement="auto"
              name="reply-to-email"
              options={companyUsers}
              onChange={email => setReplyToEmail(email)}
            />
            <Editor
              placeholder="Enter a custom message to send to your account reps."
              value={message}
              onChange={setMessage}
            />
          </Form.Group>
        </Col>
      </Row>
    );
  };

  const ctaContent = () => {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center mt-5">
        {textInputs()}
        <div className="d-flex justify-content-center gap-2 flex-wrap">
          <Button label="Don't Send" variant="outline" onClick={postDontSend}/>
          <Button disabled={sendingDisabled} label="Send Updates" onClick={postSendUpdates}/>
        </div>
      </div>
    );
  };

  const activateSnackbar = ({ text, variant }) => {
    showSnackbar({ text, variant });
  };

  const previouslySentContent = () => {
    return (
      <Modal.Body className="py-10 px-8">
        <div className="text-center">
          <FontAwesomeIcon className="icon-gradient" icon={faCircleCheck} size="3x" />
          <h6 className="fw-semibold py-2 my-4">
            Send to your account reps?
          </h6>
        </div>
        <p className="fs-large text-center mb-0 text-muted">
          Please confirm if you would like to update your account reps
        </p>

        {ctaContent()}
      </Modal.Body>
    );
  };

  const neverSentContent = () => {
    return (
      <Modal.Body className="py-10 px-8">
        <div className="text-center">
          <FontAwesomeIcon className="icon-gradient" icon={faSatelliteDish} size="3x" />
          <h6 className="py-2 my-4">
            <strong className="fw-semibold">Send to your syndicated account reps?</strong>
          </h6>
        </div>
        <p className="fs-large text-center mb-3 pb-3 text-muted">
          Please confirm if you would like to send these changes to your account reps.
        </p>
        <div>
          <label className="border rounded w-100 p-3 mb-3 d-flex align-items-center gap-2 fs-large">
            <input
              checked={manuallySentBefore === false}
              className="form-check-input m-0"
              type="radio"
              onChange={() => setManuallySentBefore(false)}
            >
            </input>
            Sending a new listing to {partnerNamesDisplay}?
          </label>
          <label className="border rounded w-100 p-3 d-flex align-items-center gap-2 fs-large">
            <input
              checked={manuallySentBefore === true}
              className="form-check-input m-0"
              type="radio"
              onChange={() => setManuallySentBefore(true)}
            >
            </input>
            Sending updates on an existing listing to {partnerNamesDisplay}?
          </label>
        </div>

        {ctaContent()}
      </Modal.Body>
    );
  };

  return (
    <Modal show={show} onHide={hideModal}>
      {previouslySent ? previouslySentContent() : neverSentContent()}
    </Modal>
  );
}

SendRepEmailModal.propTypes = {
  afterSend: PropTypes.func.isRequired,
  companyUsers: PropTypes.array.isRequired,
  partnerNames: PropTypes.arrayOf(PropTypes.string).isRequired,
  previouslySent: PropTypes.bool.isRequired,
  sendEmailSyndicationPath: PropTypes.string.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};
